// src/rms/ads/adsense.js
// ============================================================================
// Google AdSense as network fill for the operator rails
// ============================================================================
// Everything here is inert until VITE_ADSENSE_CLIENT is set, which is what
// keeps development, the local demo backend and the twelve seeded accounts
// looking the way they always have: no client id, no network fill, house
// inventory as before.
//
// ----------------------------------------------------------------------------
// Why AdSense cannot go through AdUnit's `type: "html"` path
// ----------------------------------------------------------------------------
// That path renders a network snippet inside a sandboxed iframe WITHOUT
// allow-same-origin, which is the right call for an affiliate tag: the
// advertiser's script gets no access to the operator's DOM, storage or
// session. adsbygoogle.js cannot run under those terms -- it needs page-origin
// access and the real page URL for policy and targeting -- so it renders
// directly in the document as its own creative type, and is treated as more
// trusted than an affiliate snippet because it has to be. That is a real
// reduction in isolation and it is the price of this feature.
//
// ----------------------------------------------------------------------------
// Two policy constraints shape the design, not just the code
// ----------------------------------------------------------------------------
// 1. THE RAILS ROTATE, ADSENSE MUST NOT. Slots rotate creatives every 32-45
//    seconds. Tearing down an ad unit and pushing a fresh one on a timer is
//    impression inflation, which is the fastest way to lose an AdSense
//    account. The campaign id here is therefore STABLE per slot, so a rotation
//    resolves to the same campaign, React reconciles the same element, and no
//    second push ever happens. If you change the id to include the nonce, you
//    have built the violation.
//
// 2. ONE UNIT PER PLACEMENT. Three rail rectangles plus two mastheads would be
//    five network units on a screen that is mostly form controls -- exactly
//    the shape "valuable inventory" enforcement looks for. adEngine offers
//    AdSense to the FIRST slot of a placement only; the rest fall through to
//    house inventory, which also keeps the rail looking full rather than
//    stacked with identical grey boxes.
//
// The ad-free tier and the transactional surfaces need no handling here: both
// are decided in selectAd/selectAdStack before fill is ever considered, so
// AdSense inherits them. A paying customer sees nothing, and checkout stays
// clean, for the same reason they always did.
// ============================================================================

const CLIENT = import.meta.env.VITE_ADSENSE_CLIENT || "";

/** Ad unit ids, one per IAB geometry the slot table actually uses. */
const SLOT_IDS = {
  sidebar: import.meta.env.VITE_ADSENSE_SLOT_SIDEBAR || "",
  banner_top: import.meta.env.VITE_ADSENSE_SLOT_BANNER || "",
};

export function adSenseClient() {
  return CLIENT;
}

export function adSenseSlotId(placement) {
  return SLOT_IDS[placement] || "";
}

/** Both halves must be present: a client with no unit id renders a dead box. */
export function isAdSenseConfigured(placement) {
  return Boolean(CLIENT && SLOT_IDS[placement]);
}

/**
 * A campaign-shaped object so the rest of the engine needs no special case.
 *
 * `id` is stable per placement. That is load-bearing -- see note 1 above -- and
 * it is also what limits AdSense to one unit per placement, because
 * selectAdStack refuses to place the same campaign id twice in one stack.
 */
export function adSenseCampaign(placement) {
  return {
    id: `adsense-${placement}`,
    advertiser: "",
    isAdsense: true,
    placements: [placement],
    cpmCents: 0,
    weight: 1,
    creative: { type: "adsense", placement },
  };
}

// ----------------------------------------------------------------------------
// Script loading
// ----------------------------------------------------------------------------
// Loaded on first use rather than from index.html, so Google's script is not
// running on the landing page, the media kit, the join page or an ad-free
// operator's dashboard -- none of which will ever show a unit. One promise,
// shared, so three rail slots mounting together fetch it once.

let scriptPromise = null;

export function loadAdSense() {
  if (!CLIENT) return Promise.reject(new Error("AdSense is not configured."));
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=" +
      encodeURIComponent(CLIENT);
    el.async = true;
    el.crossOrigin = "anonymous";
    el.onload = resolve;
    // Blocked by an ad blocker, offline, or refused. Rejecting rather than
    // hanging lets the unit remove itself and the rail close the gap.
    el.onerror = () => reject(new Error("The AdSense script could not be loaded."));
    document.head.appendChild(el);
  });

  return scriptPromise;
}

export default { adSenseClient, adSenseSlotId, isAdSenseConfigured, adSenseCampaign, loadAdSense };
